import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import { requestsService, Request } from "../services/requestsService";
import { useSelector } from "react-redux";
import { selectUserId } from "../store/slices/authUserSlice";
import RequestEditionGenerator from "./RequestEditionGenerator";

const SingleRequest: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const [request, setRequest] = useState<Request | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContext, setEditedContext] = useState("");
  const [showGenerator, setShowGenerator] = useState(false);
  const userId = useSelector(selectUserId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId || !requestId) return;

    setIsLoading(true);
    requestsService.getRequestById(requestId)
      .then((data) => {
        setRequest(data);
        setEditedContext(data.context || "");
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching request:", error);
        setIsLoading(false);
      });
  }, [requestId, userId]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatRequestType = (type: string) => {
    switch(type) {
      case 'voice_note':
        return 'Voice Note';
      case 'link':
        return 'Link';
      case 'document':
        return 'Document';
      case 'self_interview':
        return 'AI Interview';
      case 'professional_interview':
        return 'Professional Interview';
      default:
        return type;
    }
  };

  const isInterviewType = request?.request_type === 'self_interview' || request?.request_type === 'professional_interview';

  const formatStatus = (status: string) => {
    switch(status) {
      case 'processing':
        return 'Processing';
      case 'done':
        return 'Done';
      case 'not_used':
        return 'Not Used';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'processing':
        return '#fbbf24'; // yellow
      case 'done':
        return '#34d399'; // green
      case 'not_used':
        return '#9ca3af'; // gray
      default:
        return '#6b7280';
    }
  };

  const handleSaveEdit = async () => {
    if (!request || !requestId) return;
    
    try {
      const updated = await requestsService.updateRequest(requestId, { context: editedContext });
      setRequest(updated);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating request:", error);
      alert("Failed to update request");
    }
  };

  const handleMarkDone = async () => {
    if (!request || !requestId) return;

    if (!window.confirm("Mark this request as done?")) return;

    try {
      const updated = await requestsService.updateRequest(requestId, { status: 'done' });
      setRequest(updated);
    } catch (error) {
      console.error("Error marking request as done:", error);
      alert("Failed to mark request as done");
    }
  };

  const handleDelete = async () => {
    if (!requestId) return;

    if (window.confirm("Are you sure you want to delete this request?")) {
      try {
        await requestsService.deleteRequest(requestId);
        navigate('/requests/list');
      } catch (error) {
        console.error("Error deleting request:", error);
        alert("Failed to delete request");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="empty-state-container">
        <h3>Request not found</h3>
        <p>The request you're looking for doesn't exist or has been removed.</p>
        <Link to="/requests/list" className="primary-button">
          Back to Requests
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px" }}>
        <Link to="/requests/list" className="back-link">
          ← Back to requests
        </Link>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <span style={{ color: "#666", fontSize: "14px" }}>
            {formatDate(request.created_at)}
          </span>
          {!isInterviewType && (
            <button
              className="primary-button"
              onClick={() => setShowGenerator(true)}
              disabled={!request.context}
              style={{ opacity: request.context ? 1 : 0.5 }}
            >
              Generate Email
            </button>
          )}
          {request.status !== 'done' && (
            <button
              className="tertiary-info-button"
              onClick={handleMarkDone}
            >
              Mark as Done
            </button>
          )}
          {!isInterviewType && (
            <button
              className="tertiary-info-button"
              onClick={handleDelete}
              style={{ color: "#dc2626" }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: "24px" }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
          marginTop: "24px"
        }}>
          <h1 style={{ marginBottom: "8px" }}>
            {formatRequestType(request.request_type)} Request
          </h1>
          <div style={{ display: "flex", gap: "8px" }}>
            <span className={`list-tag-${request.request_type === 'voice_note' ? 'purple' : request.request_type === 'link' ? 'blue' : 'green'}`}>
              {formatRequestType(request.request_type)}
            </span>
            <span 
              style={{
                padding: "10px 20px",
                fontSize: ".85rem",
                fontWeight: 500,
                borderRadius: "10px",
                backgroundColor: getStatusColor(request.status),
                color: "white"
              }}
            >
              {formatStatus(request.status)}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <p style={{ color: "#666", fontSize: "14px" }}>
            Created by: {request.user.name || request.user.email}
          </p>
        </div>

        {showGenerator && request.context && requestId && (
          <RequestEditionGenerator
            requestId={requestId}
            requestContext={request.context}
            requestType={request.request_type}
            onClose={() => setShowGenerator(false)}
          />
        )}

        {/* Custom UI for AI interview requests */}
        {isInterviewType && request.files && request.files.length > 0 && (request.files[0] as any)?.question && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={{ fontSize: "16px", color: "#374151", marginBottom: "16px" }}>
              Interview Responses ({request.files.length})
            </h3>
            {request.files.map((qa: any, i: number) => {
              const pillarLabel = qa.pillar ? qa.pillar.charAt(0).toUpperCase() + qa.pillar.slice(1) : "";
              return (
                <div key={i} style={{
                  padding: "16px", marginBottom: "12px", borderRadius: "8px",
                  border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    {pillarLabel && (
                      <span style={{
                        fontSize: "11px", fontWeight: 500, padding: "2px 8px",
                        borderRadius: "99px", backgroundColor: "#F3F4F6", color: "#6B7280",
                      }}>
                        {pillarLabel}
                      </span>
                    )}
                    {qa.quality_score && (
                      <span style={{
                        fontSize: "11px", fontWeight: 600, padding: "2px 8px",
                        borderRadius: "99px",
                        backgroundColor: qa.quality_score === "Good" ? "#F0FDF4" : qa.quality_score === "Okay" ? "#FFFBEB" : "#FEF2F2",
                        color: qa.quality_score === "Good" ? "#16A34A" : qa.quality_score === "Okay" ? "#D97706" : "#DC2626",
                      }}>
                        {qa.quality_score}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: "14px", fontWeight: 600, color: "#111827", marginBottom: "8px", lineHeight: 1.4 }}>
                    {qa.question}
                  </p>
                  <p style={{ fontSize: "14px", color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                    {qa.answer}
                  </p>
                  {qa.article_url && (
                    <a
                      href={qa.article_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "12px", color: "#6366F1", textDecoration: "none", marginTop: "8px", display: "inline-block" }}
                    >
                      {qa.article_title || qa.article_url} &rarr;
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isInterviewType && request.links && request.links.length > 0 && (
          <div style={{
            padding: "16px",
            marginBottom: "24px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e2e8f0"
          }}>
            <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#374151" }}>
              Links ({request.links.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {request.links.map((link, index) => (
                <a 
                  key={index}
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: "#3182ce", 
                    textDecoration: "none", 
                    wordBreak: "break-all",
                    padding: "8px 0",
                    borderBottom: index < request.links.length - 1 ? "1px solid #e5e7eb" : "none"
                  }}
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}

        {!isInterviewType && (request.context || request.request_type === 'voice_note') && (
          <div style={{
            padding: "16px",
            marginBottom: "24px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e2e8f0"
          }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "12px"
            }}>
              <h3 style={{ fontSize: "16px", color: "#374151", margin: 0 }}>
                {request.request_type === 'voice_note' ? 'Transcript' : 'Context'}
              </h3>
              {!isEditing && (
                <button
                  className="tertiary-info-button"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </button>
              )}
            </div>
            
            {isEditing ? (
              <div>
                <textarea
                  value={editedContext}
                  onChange={(e) => setEditedContext(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    resize: "vertical"
                  }}
                />
                <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                  <button className="primary-button" onClick={handleSaveEdit}>
                    Save
                  </button>
                  <button 
                    className="secondary-button" 
                    onClick={() => {
                      setIsEditing(false);
                      setEditedContext(request.context || "");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                {request.context || "No content available"}
              </div>
            )}
          </div>
        )}

        {!isInterviewType && request.files && request.files.length > 0 && (
          <div style={{
            padding: "16px",
            marginBottom: "24px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e2e8f0"
          }}>
            <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#374151" }}>
              Attached Files ({request.files.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {request.files.map((file, index) => (
                <div 
                  key={index}
                  style={{ 
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: index < request.files.length - 1 ? "1px solid #e5e7eb" : "none"
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span style={{ fontWeight: "500", color: "#374151" }}>
                      {file.name}
                    </span>
                    <div style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#6b7280" }}>
                      <span>{file.type}</span>
                      <span>{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>
                  {file.url && (
                    <a
                      href={file.url}
                      download={file.name}
                      className="tertiary-info-button"
                      style={{
                        textDecoration: "none",
                        fontSize: "14px"
                      }}
                    >
                      Download
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{
          padding: "16px",
          marginBottom: "24px",
          backgroundColor: "#f0f8ff",
          borderRadius: "8px",
          border: "1px solid #e0e7eb",
          fontSize: "14px",
          lineHeight: "1.5"
        }}>
          <p style={{ margin: "0 0 8px 0", fontWeight: "500", color: "#374151" }}>
            📝 Content Integration
          </p>
          <p style={{ margin: 0, color: "#6b7280" }}>
            We will incorporate this request into your next piece of content. There's a small chance we may choose not to include it if the content seems better without it, but you can always add it yourself if needed.
          </p>
        </div>

      </div>
    </div>
  );
};

export default SingleRequest;